/**
 * Constructs a URL by combining a base URL, an optional path, and an optional set of query parameters.
 *
 * @note the values in the `args` object can be either strings or numbers. They are converted to strings before being
 * appended as query parameters.
 *
 * @param baseUrl - The base URL to which the path and query parameters will be appended.
 * @param path - An optional path to be appended to the base URL. Defaults to an empty string.
 * @param args - An optional object representing query parameters in key-value pairs. Defaults to an empty object.
 * @returns The fully constructed URL as a string.
 *
 * @example
 * const url = makeUrl('https://example.com', '/api/data', { userId: 123, filter: 'active' });
 * // Returns 'https://example.com/api/data?userId=123&filter=active'
 */
export function makeUrl(
  baseUrl: string,
  path: string = '',
  args: { [key: string]: string | number | boolean } = {}
): string {
  const url = new URL(baseUrl)
  url.pathname = joinPath(url.pathname, path)

  for (const key in args) {
    url.searchParams.append(key, args[key].toString())
  }

  return url.toString()
}

/**
 * Joins all given path segments together using a forward slash ('/') as a delimiter,
 * similar to Node.js's native `path.join` method.
 *
 * This function accepts any number of path segments as arguments. It processes each segment by:
 * 1. Trimming leading and trailing whitespace.
 * 2. Removing trailing slashes from the first segment.
 * 3. Removing leading and trailing slashes from subsequent segments.
 *
 * After processing, the function filters out any empty segments and then joins the remaining segments with a single slash ('/').
 * This approach ensures that the resultant path doesn't have redundant or trailing slashes, mimicking the behavior of `path.join`
 * in Node.js, but for URL or Unix-style paths.
 *
 * @param args - An arbitrary number of string arguments representing path segments.
 * @returns A single string representing the joined path.
 *
 * @example
 * const fullPath = joinPath('/foo/', '/bar', 'baz/');
 * // Returns '/foo/bar/baz'
 */
function joinPath(...args: string[]) {
  const lastPart = args[args.length - 1]
  return (
    args
      .map((part, i) => {
        if (i === 0) {
          return part.trim().replace(/\/*$/g, '')
        }
        return part.trim().replace(/(^\/*|\/*$)/g, '')
      })
      .filter((x) => x.length)
      .join('/') + (lastPart.endsWith('/') ? '/' : '')
  )
}

export const THIS_BASE_HOST = `${window.location.protocol}//${window.location.host}`
